import { useSecureService } from '../store/service';
import { findVersionByHash, pickLatestVersion } from '../utils/fileDetailVersionPolicy';
import { useAntdAppMessage } from './useAntdAppMessage';

function resolveVersionToDelete(options: {
  fileDetail: any;
  selectedVersion: number;
  versions: any[];
  hash: string;
}): any | null {
  const { fileDetail, selectedVersion, versions, hash } = options;
  const allVersions = fileDetail?.versions ?? [];

  if (selectedVersion && allVersions.length > 0) {
    const fromSelected = allVersions.find((v: any) => v.id === selectedVersion);
    if (fromSelected) return fromSelected;
  }

  if (hash && allVersions.length > 0) {
    const fromHash = findVersionByHash(allVersions, hash);
    if (fromHash) return fromHash;
  }

  if (allVersions.length > 0) {
    return pickLatestVersion(allVersions) ?? allVersions[0];
  }

  if (selectedVersion && versions.length > 0) {
    return versions.find((v: any) => v.id === selectedVersion) ?? null;
  }

  if (hash && versions.length > 0) {
    return findVersionByHash(versions, hash);
  }

  return versions.length > 0 ? pickLatestVersion(versions) ?? versions[0] : null;
}

interface UseFileOperationsProps {
  fileDetail: any;
  selectedVersion: number;
  versions: any[];
  location: any;
}

export const useFileOperations = ({
  fileDetail,
  selectedVersion,
  versions,
  location
}: UseFileOperationsProps) => {
  const message = useAntdAppMessage();
  const { deleteEntry } = useSecureService();

  // Handle file/version deletion
  const handleDelete = async () => {
    if (!fileDetail?.id) {
      throw new Error('File information not found.');
    }

    try {
      const hash = location.hash ? location.hash.substring(1) : '';
      const targetVersion = resolveVersionToDelete({
        fileDetail,
        selectedVersion,
        versions,
        hash,
      });

      const entriesToDelete = targetVersion
        ? [{ id: targetVersion.id, entryType: 'Version' as const }]
        : [{ id: fileDetail.id, entryType: 'File' as const }];

      const response = await deleteEntry(entriesToDelete);

      if (response.success) {
        // Successful deletion, reload page or redirect
        window.location.reload();
      } else {
        throw new Error(response.message || 'File could not be deleted.');
      }
    } catch (error) {
      throw error;
    }
  };

  // Handle file download
  const handleDownload = async () => {
    if (!fileDetail?.versions || fileDetail.versions.length === 0) {
      message.error('No downloadable file found.');
      return;
    }

    // Prefer selected version if provided, otherwise fall back to first version
    const selectedVersionData = (selectedVersion && fileDetail.versions.find((v: any) => v.id === selectedVersion)) || fileDetail.versions[0];
    if (!selectedVersionData?.fileUrl) {
      message.error('File URL not found.');
      return;
    }

    try {
      // Build filename with version suffix
      const baseName = (fileDetail.name || 'download').toString();
      const versionName: string = selectedVersionData.name || (selectedVersionData.id ? `v${selectedVersionData.id}` : 'v1');
      const fileExtension = selectedVersionData.fileUrl.split('.').pop()?.split('?')[0] || 'mp4';
      const fullFileName = `${baseName}-${versionName}.${fileExtension}`;

      // Fetch file and download as blob
      const response = await fetch(selectedVersionData.fileUrl);
      if (!response.ok) {
        throw new Error('File could not be retrieved');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = fullFileName;
      
      // Click link and clean up
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL
      window.URL.revokeObjectURL(url);

      message.success('File download started.');
    } catch (error) {
      message.error('File download could not be started.');
    }
  };

  // Handle URL copy
  const handleCopyUrl = async () => {
    try {
      // Tüm yolu (hash dahil) kopyala
      const urlsToCopy = window.location.href;
      
      // Use Clipboard API (for modern browsers)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(urlsToCopy);
        message.success('URL copied to clipboard!');
      } else {
        // Fallback: for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = urlsToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          message.success('URL copied to clipboard!');
        } catch (err) {
          message.error('URL could not be copied.');
        }
        
        document.body.removeChild(textArea);
      }
    } catch (error) {
      message.error('URL could not be copied.');
    }
  };

  return {
    handleDelete,
    handleDownload,
    handleCopyUrl
  };
}; 